import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Tag,
  Badge,
  Dropdown,
  Menu,
  Modal,
  Form,
  message,
  Row,
  Col,
  Statistic,
  Progress,
  Tooltip,
  Typography,
  Drawer,
  Avatar,
  List,
  Timeline,
  Popconfirm,
  Upload,
  Divider,
  Alert,
  Tabs
} from 'antd'
import {
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  FileTextOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  SettingOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  WalletOutlined,
  FileProtectOutlined,
  BarChartOutlined
} from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMoneyBillWave,
  faClipboardList,
  faUserClock,
  faExclamationTriangle,
  faHandHoldingDollar,
  faFileInvoiceDollar,
  faCalendarAlt,
  faEnvelope,
  faSms,
  faPhone,
  faDownload,
  faFilter,
  faSort,
  faTags,
  faUsers,
  faChartLine
} from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
// Import our backend APIs instead of mock data
import {
  getFollowUpAccounts,
  getFollowUpStats,
  updateAccountStatus,
  updateFollowUpAccount,
  updateFollowUpAccountById,
  bulkUpdateFollowUpAccounts,
  recordFollowUpActivity,
  deleteAllFollowUpAccounts
} from '../../backend-integration/api'
import { recordPhoneCall, recordEmail, recordSMS } from '../../backend-integration/api'
import { formatCurrency, getStatusColor, getAgingColor } from '../../utils/helpers'
import './FollowUpManager.css'
import AccountDetails from './AccountDetails/AccountDetails'
import ResetBillingData from '../DebugTransactions/ResetBillingData'

const { Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

const FollowUpManager = () => {
  const [loading, setLoading] = useState(false)
  const [followUpData, setFollowUpData] = useState([])
  const [filteredData, setFilteredData] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState(null)
  const [filterAgingBucket, setFilterAgingBucket] = useState(null)
  const [filterAssignee, setFilterAssignee] = useState(null)
  const [filterAccountGroup, setFilterAccountGroup] = useState(null)
  const [dateRange, setDateRange] = useState([])
  const [bulkActionVisible, setBulkActionVisible] = useState(false)
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [dashboardStats, setDashboardStats] = useState({
    totalOutstanding: 0,
    totalAccounts: 0,
    overdueAccounts: 0,
    collectionRate: 0
  })
  const [form] = Form.useForm()
  const [accountDetailsDrawerVisible, setAccountDetailsDrawerVisible] = useState(false)
  // Pagination states
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)

  // Load initial data
  useEffect(() => {
    loadFollowUpData()
    loadDashboardStats()
  }, [])

  // Reload data when filters change
  useEffect(() => {
    loadFollowUpData()
  }, [searchText, filterStatus, filterAgingBucket, filterAssignee, filterAccountGroup, dateRange, page, pageSize])

  const loadFollowUpData = async () => {
    setLoading(true)
    try {
      // Build filter object based on current filters
      let filter = {}

      if (searchText) {
        filter.$or = [
          { patientName: { $regex: searchText, $options: 'i' } },
          { billingId: { $regex: searchText, $options: 'i' } },
          { fileNo: { $regex: searchText, $options: 'i' } },
          { medicalAid: { $regex: searchText, $options: 'i' } }
        ]
      }

      if (filterStatus) {
        filter.status = filterStatus
      }

      if (filterAgingBucket) {
        filter.agingBucket = filterAgingBucket
      }

      if (filterAssignee) {
        filter.assignee = filterAssignee
      }

      if (filterAccountGroup) {
        filter.accountGroup = filterAccountGroup
      }

      if (dateRange && dateRange.length === 2) {
        filter.lastInvoiceDate = {
          $gte: dateRange[0].format('YYYY-MM-DD'),
          $lte: dateRange[1].format('YYYY-MM-DD')
        }
      }

      // Fetch data from API
      const response = await getFollowUpAccounts(filter, page, pageSize, '', { daysOutstanding: -1 })

      // Handle both response formats (direct array or object with data property)
      if (response) {
        const data = Array.isArray(response) ? response : response.data || []
        const total = Array.isArray(response) ? response.length : response.total || data.length

        // Import ledger API
        const { getPatientLedgerSummary } = await import('../../backend-integration/api')

        // Update each account with actual ledger balance
        const updatedData = await Promise.all(
          data.map(async (account) => {
            const billingId = account.billingId

            try {
              // Get actual balance from ledger
              const summary = await getPatientLedgerSummary(billingId, account.patientId)

              if (summary) {
                return {
                  ...account,
                  outstandingAmount: summary.currentBalance || 0,
                  patientOutstanding: summary.patientBalance || 0,
                  medicalAidOutstanding: summary.medicalAidBalance || 0
                }
              }
            } catch (err) {
              console.warn('Error getting ledger summary for account:', billingId, err)
            }

            // Return original account if error
            return account
          })
        )

        setFollowUpData(updatedData)
        setTotal(total)
        setFilteredData(updatedData) // Since filtering is done server-side
      } else {
        setFollowUpData([])
        setFilteredData([])
        setTotal(0)
      }
    } catch (error) {
      console.error('Failed to load follow-up data:', error)
      message.error('Failed to load follow-up data')
      setFollowUpData([])
      setFilteredData([])
    } finally {
      setLoading(false)
    }
  }

  const loadDashboardStats = async () => {
    try {
      // Get all accounts from follow-up accounts to get list of billingIds
      const allAccounts = await getFollowUpAccounts({}, 1, 1000) // Get more accounts for stats

      let totalOutstanding = 0
      let totalAccounts = 0
      let overdueAccounts = 0

      if (allAccounts && allAccounts.length > 0) {
        // For each unique billingId, get the actual balance from ledger
        const processedBillingIds = new Set()

        for (const account of allAccounts) {
          const billingId = account.billingId

          // Skip if we already processed this billingId
          if (processedBillingIds.has(billingId)) continue
          processedBillingIds.add(billingId)

          try {
            // Get actual balance from ledger (same logic as account details)
            const { getPatientLedgerSummary } = await import('../../backend-integration/api')
            const summary = await getPatientLedgerSummary(billingId, account.patientId)

            if (summary && summary.currentBalance > 0) {
              totalOutstanding += summary.currentBalance
              totalAccounts++

              // Check if overdue (90+ days)
              if (account.agingBucket === '90+') {
                overdueAccounts++
              }
            }
          } catch (err) {
            console.warn('Error getting ledger summary for account:', billingId, err)
          }
        }
      }

      setDashboardStats({
        totalOutstanding,
        totalAccounts,
        overdueAccounts,
        collectionRate: totalAccounts > 0 ? (((totalAccounts - overdueAccounts) / totalAccounts) * 100).toFixed(1) : 0
      })
    } catch (error) {
      console.error('Failed to load dashboard stats:', error)
      message.error('Failed to load dashboard statistics')
    }
  }

  const handleStatusChange = async (record, newStatus) => {
    try {
      await updateAccountStatus(record._id, newStatus, 'Status updated via follow-up manager')
      message.success(`Status updated to ${newStatus}`)
      loadFollowUpData() // Reload data
    } catch (error) {
      console.error('Failed to update status:', error)
      message.error('Failed to update status')
    }
  }

  const handleFollowUpDateChange = async (record, newDate) => {
    try {
      await updateFollowUpAccountById(record._id, {
        nextFollowUpDate: newDate ? newDate.format('YYYY-MM-DD') : null
      })
      message.success('Follow-up date updated')
      loadFollowUpData() // Reload data
    } catch (error) {
      console.error('Failed to update follow-up date:', error)
      message.error('Failed to update follow-up date')
    }
  }

  const handleBulkAction = async (action) => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select accounts to perform bulk action')
      return
    }

    try {
      switch (action) {
        case 'update_status':
          // Handle in modal
          setBulkActionVisible(true)
          break
        case 'send_reminders':
          // Record email activity for each selected account
          const emailPromises = selectedRowKeys.map((accountId) =>
            recordEmail({
              accountId,
              billingId: followUpData.find((acc) => acc._id === accountId)?.billingId,
              subject: 'Payment Reminder',
              template: 'payment_reminder',
              outcome: 'sent',
              notes: 'Bulk reminder sent'
            })
          )
          await Promise.all(emailPromises)
          message.success(`Reminders sent to ${selectedRowKeys.length} accounts`)
          setSelectedRowKeys([])
          loadFollowUpData()
          break
        case 'export_selected':
          message.success(`Exported ${selectedRowKeys.length} accounts`)
          break
        case 'assign_to':
          // Handle in modal
          setBulkActionVisible(true)
          break
        default:
          break
      }
    } catch (error) {
      console.error('Bulk action failed:', error)
      message.error('Bulk action failed')
    }
  }

  const handleExport = (format) => {
    const dataToExport =
      selectedRowKeys.length > 0 ? filteredData.filter((item) => selectedRowKeys.includes(item._id)) : filteredData

    // TODO: Implement actual export functionality
    message.success(`Exported ${dataToExport.length} records as ${format.toUpperCase()}`)
  }

  const statusOptions = [
    { value: 'awaiting_payment_patient', label: 'Awaiting Pmt: Patient', color: 'orange' },
    { value: 'awaiting_payment_aid', label: 'Awaiting Pmt: Medical Aid', color: 'blue' },
    { value: 'handed_over', label: 'Handed Over', color: 'red' },
    { value: 'payment_plan', label: 'Pmt Plan', color: 'purple' },
    { value: 'disputed', label: 'Disputed', color: 'volcano' },
    { value: 'completed', label: 'Completed', color: 'green' },
    { value: 'coid', label: 'COID', color: 'geekblue' },
    { value: 'fem', label: 'FEM', color: 'magenta' },
    { value: 'saps', label: 'SAPS', color: 'cyan' },
    { value: 'rma', label: 'RMA', color: 'lime' }
  ]

  const agingBuckets = [
    { value: '0-29', label: '0-29 days', color: 'green' },
    { value: '30-59', label: '30-59 days', color: 'orange' },
    { value: '60-89', label: '60-89 days', color: 'red' },
    { value: '90+', label: '90+ days', color: 'volcano' }
  ]

  const assigneeOptions = [
    { value: 'chantelle', label: 'Chantelle Bosch' },
    { value: 'alvida', label: 'Alvida' },
    { value: 'admin_1', label: 'Admin 1' },
    { value: 'admin_2', label: 'Admin 2' }
  ]

  const accountGroupOptions = [
    { value: 'private', label: 'Private' },
    { value: 'medical_aid', label: 'Medical Aid' },
    { value: 'coid', label: 'COID' },
    { value: 'fem', label: 'FEM' },
    { value: 'saps', label: 'SAPS' },
    { value: 'rma', label: 'RMA' }
  ]

  const columns = [
    {
      title: 'Patient',
      dataIndex: 'patientName',
      key: 'patientName',
      width: 200,
      fixed: 'left',
      render: (text, record) => (
        <div className='billing-patient-cell'>
          <Avatar icon={<UserOutlined />} size='small' style={{ flexShrink: 0 }} />
          <div className='billing-patient-info'>
            <Tooltip title={text} placement='topLeft'>
              <div className='billing-patient-name'>{text}</div>
            </Tooltip>
            <div className='billing-patient-details'>
              <Tooltip title={`Billing ID: ${record.billingId}`}>
                <span className='billing-patient-detail-item'>{record.billingId}</span>
              </Tooltip>
              {record.fileNo && (
                <Tooltip title={`File: ${record.fileNo}`}>
                  <span className='billing-patient-detail-item'>• File: {record.fileNo}</span>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Outstanding Amount',
      dataIndex: 'outstandingAmount',
      key: 'outstandingAmount',
      width: 150,
      align: 'right',
      render: (amount) => (
        <Tooltip title={`Outstanding: ${formatCurrency(amount)}`} placement='topLeft'>
          <div
            className='billing-currency'
            style={{
              color: amount > 0 ? '#cf1322' : '#52c41a',
              fontWeight: 'bold'
            }}
          >
            {formatCurrency(amount)}
          </div>
        </Tooltip>
      ),
      sorter: (a, b) => a.outstandingAmount - b.outstandingAmount
    },
    {
      title: 'Aging',
      dataIndex: 'agingBucket',
      key: 'agingBucket',
      width: 100,
      render: (aging, record) => (
        <div className='billing-table-tags-container'>
          <Tooltip title={`Aging bucket: ${aging}`}>
            <Tag color={getAgingColor(aging)} className='billing-table-tag billing-table-tag-with-tooltip'>
              {aging}
            </Tag>
          </Tooltip>
        </div>
      ),
      filters: agingBuckets.map((bucket) => ({
        text: bucket.label,
        value: bucket.value
      })),
      onFilter: (value, record) => record.agingBucket === value
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status, record) => (
        <Select
          value={status}
          onChange={(newStatus) => handleStatusChange(record, newStatus)}
          className='billing-table-status-select'
          size='small'
          dropdownMatchSelectWidth={false}
        >
          {statusOptions.map((option) => (
            <Option key={option.value} value={option.value}>
              <Tag color={option.color} style={{ margin: 0 }} className='billing-table-tag'>
                {option.label}
              </Tag>
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: 'Account Group',
      dataIndex: 'accountGroup',
      key: 'accountGroup',
      width: 120,
      render: (group) => (
        <div className='billing-table-tags-container'>
          <Tooltip title={`Account Group: ${group.toUpperCase()}`}>
            <Tag
              color={group === 'private' ? 'blue' : 'geekblue'}
              className='billing-table-tag billing-table-tag-with-tooltip'
            >
              {group.toUpperCase()}
            </Tag>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Assignee',
      dataIndex: 'assignee',
      key: 'assignee',
      width: 120,
      render: (assignee) => {
        const assigneeName = assigneeOptions.find((opt) => opt.value === assignee)?.label || assignee
        return (
          <Tooltip title={`Assigned to: ${assigneeName}`} placement='topLeft'>
            <div className='billing-table-cell-ellipsis' style={{ fontSize: '12px' }}>
              {assigneeName}
            </div>
          </Tooltip>
        )
      }
    },
    {
      title: 'Next Follow-up',
      dataIndex: 'nextFollowUpDate',
      key: 'nextFollowUpDate',
      width: 150,
      render: (date, record) => (
        <DatePicker
          value={date ? dayjs(date) : null}
          onChange={(newDate) => handleFollowUpDateChange(record, newDate)}
          size='small'
          className='billing-table-date-picker'
          placeholder='Set date'
        />
      )
    },
    {
      title: 'Last Contact',
      dataIndex: 'lastContactDate',
      key: 'lastContactDate',
      width: 120,
      render: (date) => {
        const formattedDate = date ? dayjs(date).format('DD MMM YYYY') : 'Never'
        return (
          <Tooltip title={`Last Contact: ${formattedDate}`} placement='topLeft'>
            <div className='billing-table-cell-ellipsis' style={{ fontSize: '12px' }}>
              {formattedDate}
            </div>
          </Tooltip>
        )
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <div className='billing-table-actions'>
          <Tooltip title='View Details'>
            <Button
              type='text'
              icon={<FileTextOutlined />}
              onClick={() => {
                setSelectedRecord(record)
                setDetailsDrawerVisible(true)
              }}
            />
          </Tooltip>
          <Tooltip title='Account'>
            <Button
              type='text'
              icon={<WalletOutlined />}
              onClick={() => {
                // Ensure billingId is included
                const recordWithBillingId = {
                  ...record,
                  billingId: record.billingId
                }
                setSelectedRecord(recordWithBillingId)
                setAccountDetailsDrawerVisible(true)
              }}
            />
          </Tooltip>
          <Tooltip title='Send Reminder'>
            <Button
              type='text'
              icon={<MailOutlined />}
              onClick={async () => {
                try {
                  await recordEmail({
                    accountId: record._id,
                    billingId: record.billingId,
                    subject: 'Payment Reminder',
                    template: 'payment_reminder',
                    outcome: 'sent',
                    notes: `Reminder sent to ${record.patientName}`
                  })
                  message.success(`Reminder sent to ${record.patientName}`)
                  loadFollowUpData()
                } catch (error) {
                  console.error('Failed to send reminder:', error)
                  message.error('Failed to send reminder')
                }
              }}
            />
          </Tooltip>
        </div>
      )
    }
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      name: record._id
    })
  }

  const bulkActionMenu = (
    <Menu
      onClick={({ key }) => handleBulkAction(key)}
      items={[
        {
          key: 'update_status',
          label: 'Update Status',
          icon: <EditOutlined />
        },
        {
          key: 'assign_to',
          label: 'Assign To',
          icon: <UserOutlined />
        },
        {
          key: 'send_reminders',
          label: 'Send Reminders',
          icon: <MailOutlined />
        },
        {
          key: 'export_selected',
          label: 'Export Selected',
          icon: <ExportOutlined />
        }
      ]}
    />
  )

  const exportMenu = (
    <Menu
      onClick={({ key }) => handleExport(key)}
      items={[
        {
          key: 'csv',
          label: 'Export as CSV',
          icon: <FontAwesomeIcon icon={faDownload} />
        },
        {
          key: 'pdf',
          label: 'Export as PDF',
          icon: <FontAwesomeIcon icon={faDownload} />
        },
        {
          key: 'excel',
          label: 'Export as Excel',
          icon: <FontAwesomeIcon icon={faDownload} />
        }
      ]}
    />
  )

  return (
    <div className='follow-up-manager'>
      {/* Reset Button Row */}
      <Row justify='end' style={{ marginBottom: 16 }}>
        <Col>
          <ResetBillingData
            onReset={() => {
              // Reload all data after reset
              loadFollowUpData()
              loadDashboardStats()
            }}
          />
        </Col>
      </Row>

      {/* Dashboard Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Card size='small'>
            <Statistic
              title='Total Outstanding'
              value={dashboardStats.totalOutstanding}
              formatter={formatCurrency}
              prefix={<FontAwesomeIcon icon={faMoneyBillWave} />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size='small'>
            <Statistic
              title='Accounts'
              value={dashboardStats.totalAccounts}
              prefix={<FontAwesomeIcon icon={faUsers} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size='small'>
            <Statistic
              title='Overdue (90+ days)'
              value={dashboardStats.overdueAccounts}
              prefix={<FontAwesomeIcon icon={faExclamationTriangle} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size='small'>
            <Statistic
              title='Collection Rate'
              value={dashboardStats.collectionRate}
              suffix='%'
              prefix={<FontAwesomeIcon icon={faChartLine} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Actions Bar */}
      <Row justify='end' style={{ marginBottom: 16 }}>
        <Space>
          <Button type='primary' icon={<ReloadOutlined />} onClick={loadFollowUpData} loading={loading}>
            Refresh
          </Button>
          <Dropdown overlay={exportMenu} trigger={['click']}>
            <Button icon={<ExportOutlined />}>Export</Button>
          </Dropdown>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Delete All Follow-up Records',
                content: 'Are you sure you want to delete ALL follow-up records? This action cannot be undone.',
                okText: 'Yes, Delete All',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: async () => {
                  try {
                    await deleteAllFollowUpAccounts()
                    message.success('All follow-up records deleted successfully')
                    loadFollowUpData()
                  } catch (error) {
                    console.error('Failed to delete all records:', error)
                    message.error('Failed to delete all records')
                  }
                }
              })
            }}
          >
            Delete All
          </Button>
        </Space>
      </Row>

      {/* Filters - No Card wrapper */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Input
            placeholder='Search patients, accounts, file numbers...'
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </Col>
        <Col xs={24} sm={4}>
          <Select
            placeholder='All Statuses'
            value={filterStatus}
            onChange={setFilterStatus}
            style={{ width: '100%' }}
            allowClear
          >
            {statusOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={4}>
          <Select
            placeholder='All Aging'
            value={filterAgingBucket}
            onChange={setFilterAgingBucket}
            style={{ width: '100%' }}
            allowClear
          >
            {agingBuckets.map((bucket) => (
              <Option key={bucket.value} value={bucket.value}>
                {bucket.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={4}>
          <Select
            placeholder='All Assignees'
            value={filterAssignee}
            onChange={setFilterAssignee}
            style={{ width: '100%' }}
            allowClear
          >
            {assigneeOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={6}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            style={{ width: '100%' }}
            placeholder={['Start Date', 'End Date']}
          />
        </Col>
      </Row>

      {/* Bulk Actions - Inline with table */}
      {selectedRowKeys.length > 0 && (
        <Row justify='space-between' align='middle' style={{ marginBottom: 8, padding: '8px 0' }}>
          <Col>
            <Space>
              <Text>{selectedRowKeys.length} account(s) selected</Text>
              <Button size='small' onClick={() => setSelectedRowKeys([])}>
                Clear Selection
              </Button>
            </Space>
          </Col>
          <Col>
            <Space>
              <Dropdown overlay={bulkActionMenu} trigger={['click']}>
                <Button type='primary'>Bulk Actions</Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>
      )}

      {/* Main Table - No Card wrapper */}
      <Table
        key={`table-${filteredData.length}`}
        className='billing-module-table'
        columns={columns}
        dataSource={filteredData}
        rowSelection={rowSelection}
        loading={loading}
        scroll={{ x: 1500 }}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} accounts`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page, pageSize) => {
            setPage(page)
            setPageSize(pageSize)
          }
        }}
        rowKey='_id'
        size='small'
        bordered={false}
        style={{
          background: '#fff',
          borderRadius: '8px'
        }}
      />

      {/* Details Drawer */}
      <Drawer
        title='Account Details'
        placement='right'
        onClose={() => setDetailsDrawerVisible(false)}
        visible={detailsDrawerVisible}
        width={600}
      >
        {selectedRecord && (
          <div className='account-details'>
            <Card title='Patient Information' style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Name:</Text>
                  <br />
                  <Text>{selectedRecord.patientName}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Account No:</Text>
                  <br />
                  <Text>{selectedRecord.billingId}</Text>
                </Col>
              </Row>
              <Divider />
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Medical Aid:</Text>
                  <br />
                  <Text>{selectedRecord.medicalAid}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>File No:</Text>
                  <br />
                  <Text>{selectedRecord.fileNo || 'N/A'}</Text>
                </Col>
              </Row>
            </Card>

            <Card title='Account Summary' style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title='Outstanding Amount'
                    value={selectedRecord.outstandingAmount}
                    formatter={formatCurrency}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title='Days Outstanding'
                    value={selectedRecord.daysOutstanding}
                    suffix='days'
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
              </Row>
            </Card>

            <Card title='Follow-up History' style={{ marginBottom: 16 }}>
              <Timeline>
                {selectedRecord.followUpHistory?.map((item, index) => (
                  <Timeline.Item key={index}>
                    <div>
                      <Text strong>{item.action}</Text>
                      <br />
                      <Text type='secondary'>
                        {dayjs(item.date).format('DD MMM YYYY HH:mm')} - {item.user}
                      </Text>
                      {item.notes && (
                        <>
                          <br />
                          <Text>{item.notes}</Text>
                        </>
                      )}
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          </div>
        )}
      </Drawer>

      {/* Account Details Drawer */}
      <Drawer
        title={null}
        placement='right'
        onClose={() => setAccountDetailsDrawerVisible(false)}
        visible={accountDetailsDrawerVisible}
        width='80%'
        bodyStyle={{ padding: 0 }}
        closable={false}
      >
        {selectedRecord && (
          <AccountDetails accountData={selectedRecord} onClose={() => setAccountDetailsDrawerVisible(false)} />
        )}
      </Drawer>
    </div>
  )
}

export default FollowUpManager
